import { supabase } from '../lib/supabase';

/**
 * Calculate dashboard metrics from real data
 */
export const calculateDashboardMetrics = (projects, bids, resources) => {
  // Active Projects metric
  const activeProjects = projects?.filter(p => 
    p?.status === 'In Progress' || p?.status === 'Planning'
  );
  const activeProjectsCount = activeProjects?.length;
  
  // Projects starting this week
  const today = new Date();
  const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
  const projectsStartingThisWeek = projects?.filter(p => {
    const startDate = new Date(p.start_date);
    return startDate >= today && startDate <= weekFromNow;
  })?.length;

  // Bid Conversion Rate
  const acceptedBids = bids?.filter(b => 
    b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
  )?.length;
  const submittedBids = bids?.filter(b => 
    b?.status === 'Submitted' || b?.status === 'Accepted' || 
    b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
  )?.length;
  const conversionRate = submittedBids > 0 ? ((acceptedBids / submittedBids) * 100)?.toFixed(1) : 0;

  // Resource Utilization
  const inUseResources = resources?.filter(r => r?.status === 'In Use')?.length;
  const totalResources = resources?.length;
  const utilizationRate = totalResources > 0 ? ((inUseResources / totalResources) * 100)?.toFixed(0) : 0;

  // Total Bids
  const totalBids = bids?.length;
  const pendingBids = bids?.filter(b => b?.status === 'Submitted' || b?.status === 'Draft')?.length;

  return {
    activeProjects: {
      value: activeProjectsCount?.toString(),
      subtitle: `${projectsStartingThisWeek} starting this week`
    },
    conversionRate: {
      value: `${conversionRate}%`,
      subtitle: `${acceptedBids} of ${submittedBids} bids accepted`
    },
    utilization: {
      value: `${utilizationRate}%`,
      subtitle: `${inUseResources} of ${totalResources} in use`
    },
    totalBids: {
      value: totalBids?.toString(),
      subtitle: `${pendingBids} pending review`
    }
  };
};

/**
 * Get resource utilization by category
 */
export const getResourceUtilizationByCategory = (resources, assignments) => {
  const categories = {
    employees: { name: 'Employees', icon: 'Users' },
    equipment: { name: 'Equipment', icon: 'Wrench' },
    trucks: { name: 'Trucks & Trailers', icon: 'Truck' },
    contractors: { name: 'Contractors', icon: 'UserCog' },
    teams: { name: 'Teams', icon: 'Users2' }
  };

  return Object.entries(categories)?.map(([key, config]) => {
    const categoryResources = resources?.filter(r => r?.category === key);
    const total = categoryResources?.length;
    const utilized = categoryResources?.filter(r => r?.status === 'In Use')?.length;
    const percentage = total > 0 ? ((utilized / total) * 100)?.toFixed(1) : 0;
    
    // Count conflicts (resources assigned to overlapping projects)
    const resourceIds = categoryResources?.map(r => r?.id);
    const categoryAssignments = assignments?.filter(a => resourceIds?.includes(a?.resource_id));
    const conflicts = 0; // Simplified - would need date overlap logic

    return {
      id: key,
      name: config?.name,
      icon: config?.icon,
      utilized,
      total,
      percentage: parseFloat(percentage),
      conflicts
    };
  })?.filter(cat => cat?.total > 0); // Only show categories with resources
};

/**
 * Get conversion funnel data
 */
export const getConversionFunnelData = (bids, projects) => {
  const totalBids = bids?.length;
  const underReview = bids?.filter(b => b?.status === 'Submitted')?.length;
  const accepted = bids?.filter(b => 
    b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
  )?.length;
  const convertedProjects = projects?.filter(p => p?.converted_from_bid)?.length;
  const activeProjects = projects?.filter(p => 
    p?.status === 'In Progress' || p?.status === 'Planning'
  )?.length;

  return [
    { id: 1, label: 'Bids Submitted', value: totalBids, icon: 'FileText' },
    { id: 2, label: 'Under Review', value: underReview, icon: 'Eye' },
    { id: 3, label: 'Accepted', value: accepted, icon: 'CheckCircle' },
    { id: 4, label: 'Converted', value: convertedProjects, icon: 'ArrowRight' },
    { id: 5, label: 'Active Projects', value: activeProjects, icon: 'Briefcase' }
  ];
};

/**
 * Get active projects for timeline
 */
export const getActiveProjectsTimeline = (projects) => {
  return projects?.filter(p => p?.status === 'In Progress' || p?.status === 'Planning')?.slice(0, 4)?.map(p => ({
      id: p?.id,
      name: p?.project_name,
      customer: p?.customer_name,
      status: p?.status === 'In Progress' ? 'On Track' : 'Planning',
      startDate: p?.start_date,
      endDate: p?.end_date,
      progress: p?.completion_percentage || 0
    }));
};

/**
 * Fetch recent activities from multiple tables
 */
export const fetchRecentActivities = async () => {
  try {
    // Fetch recent projects
    const { data: recentProjects } = await supabase?.from('projects')?.select('*, user_profiles!projects_created_by_fkey(full_name, avatar_url)')?.order('created_at', { ascending: false })?.limit(3);

    // Fetch recent bids
    const { data: recentBids } = await supabase?.from('bids')?.select('*, user_profiles!bids_created_by_fkey(full_name, avatar_url)')?.order('created_at', { ascending: false })?.limit(3);

    // Fetch recent customers
    const { data: recentCustomers } = await supabase?.from('customers')?.select('*, user_profiles!customers_created_by_fkey(full_name, avatar_url)')?.order('created_at', { ascending: false })?.limit(2);

    const activities = [];

    // Add project activities
    recentProjects?.forEach(project => {
      activities?.push({
        id: `project-${project?.id}`,
        type: 'project',
        title: `Project ${project?.status === 'Completed' ? 'completed' : 'created'}: ${project?.project_name}`,
        description: `${project?.project_number} - ${project?.customer_name}`,
        userName: project?.user_profiles?.full_name || 'Unknown User',
        userAvatar: project?.user_profiles?.avatar_url || '',
        userAvatarAlt: `Avatar of ${project?.user_profiles?.full_name || 'user'}`,
        timestamp: new Date(project.created_at)
      });
    });

    // Add bid activities
    recentBids?.forEach(bid => {
      activities?.push({
        id: `bid-${bid?.id}`,
        type: 'bid',
        title: `New bid ${bid?.status === 'Converted to Project' || bid?.status === 'Converted to a Project' ? 'converted' : 'submitted'}: ${bid?.project_name}`,
        description: `${bid?.bid_number} - $${parseFloat(bid?.total_amount)?.toLocaleString()}`,
        userName: bid?.user_profiles?.full_name || 'Unknown User',
        userAvatar: bid?.user_profiles?.avatar_url || '',
        userAvatarAlt: `Avatar of ${bid?.user_profiles?.full_name || 'user'}`,
        timestamp: new Date(bid.created_at)
      });
    });

    // Add customer activities
    recentCustomers?.forEach(customer => {
      activities?.push({
        id: `customer-${customer?.id}`,
        type: 'customer',
        title: `New customer added: ${customer?.company_name}`,
        description: `Contact: ${customer?.contact_person} - ${customer?.email}`,
        userName: customer?.user_profiles?.full_name || 'Unknown User',
        userAvatar: customer?.user_profiles?.avatar_url || '',
        userAvatarAlt: `Avatar of ${customer?.user_profiles?.full_name || 'user'}`,
        timestamp: new Date(customer.created_at)
      });
    });

    // Sort by timestamp and return top 6
    return activities?.sort((a, b) => b?.timestamp - a?.timestamp)?.slice(0, 6);
  } catch (error) {
    console.error('Error fetching activities:', error);
    return [];
  }
};

/**
 * Get active user count
 */
export const getActiveUserCount = async () => {
  try {
    const { data, error } = await supabase?.from('user_profiles')?.select('id', { count: 'exact' })?.eq('is_active', true);

    if (error) throw error;
    return data?.length || 0;
  } catch (error) {
    console.error('Error fetching active users:', error);
    return 0;
  }
};

/**
 * Get prospect summary data for dashboard
 */
export const getProspectSummary = async () => {
  try {
    const today = new Date()?.toISOString();
    const sevenDaysFromNow = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString();

    // Fetch all prospects
    const { data: prospects, error: prospectsError } = await supabase
      ?.from('prospects')
      ?.select('id, status, estimated_value, created_at');

    if (prospectsError) throw prospectsError;

    // Fetch upcoming follow-ups (scheduled within next 7 days)
    const { data: followups, error: followupsError } = await supabase
      ?.from('prospect_followups')
      ?.select('id, scheduled_at, followup_status, status')
      ?.or('followup_status.eq.Scheduled,status.eq.Scheduled')
      ?.lte('scheduled_at', sevenDaysFromNow)
      ?.gte('scheduled_at', today);

    if (followupsError) throw followupsError;

    // Fetch overdue follow-ups
    const { data: overdueFollowups, error: overdueError } = await supabase
      ?.from('prospect_followups')
      ?.select('id')
      ?.or('followup_status.eq.Overdue,followup_status.eq.Scheduled,status.eq.Scheduled')
      ?.lt('scheduled_at', today);

    if (overdueError) throw overdueError;

    const total = prospects?.length || 0;

    // Pipeline stage breakdown
    const stageCounts = {
      'New Lead': 0,
      'Contacted': 0,
      'Qualified': 0,
      'Proposal Sent': 0,
      'Negotiating': 0,
      'Converted': 0,
      'Lost': 0,
      'On Hold': 0,
    };
    prospects?.forEach(p => {
      if (stageCounts?.[p?.status] !== undefined) stageCounts[p?.status]++;
    });

    // Active pipeline (not won/lost)
    const activeStages = ['New Lead', 'Contacted', 'Qualified', 'Proposal Sent', 'Negotiating'];
    const activePipeline = prospects?.filter(p => activeStages?.includes(p?.status))?.length || 0;

    // Total pipeline value
    const pipelineValue = prospects
      ?.filter(p => activeStages?.includes(p?.status))
      ?.reduce((sum, p) => sum + (parseFloat(p?.estimated_value) || 0), 0);

    // Conversion rate (Converted / total non-new)
    const converted = stageCounts?.['Converted'] || 0;
    const totalClosed = converted + (stageCounts?.['Lost'] || 0);
    const conversionRate = totalClosed > 0 ? ((converted / totalClosed) * 100)?.toFixed(0) : 0;

    return {
      total,
      activePipeline,
      pipelineValue,
      conversionRate: parseInt(conversionRate),
      followupsDue: followups?.length || 0,
      overdueFollowups: overdueFollowups?.length || 0,
      stageCounts,
    };
  } catch (error) {
    console.error('Error fetching prospect summary:', error);
    return {
      total: 0,
      activePipeline: 0,
      pipelineValue: 0,
      conversionRate: 0,
      followupsDue: 0,
      overdueFollowups: 0,
      stageCounts: {},
    };
  }
};